/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable indent */
import React, { useCallback, useEffect } from "react";
import { forEach, map, size } from "lodash";
import { useState } from "react";
import { Election } from "../types";
import { CSSTransition, TransitionGroup } from "react-transition-group";
import BarChartD3Framer from "./BarChartD3Framer";
import { getSizeScaledFromFullHD } from "./UtilityFunctions";

export enum Modes {
  Title = "title", // Introduce the election
  Candidates = "candidates", // Introduce the candidates
  PreAscending = "round", // Show the graph with no values OR remove eliminated candidates and 0 the remaining candidates
  Ascending = "round-ascending", // Animate the graph up to the vote values
  Descending = "round-descending", // Labels disappear and the eliminated bars go down
  Winner = "winner", // Show the winner text
}

export interface StandingProps {
  standing: Election;
  registerNextSlideHandler?: (arg0: () => boolean | undefined) => void;
}

const LONG_EASE = 6000;
const SHORT_EASE = 1000;

const Standing = ({ standing, registerNextSlideHandler }: StandingProps) => {
  const [roundNumber, setRoundNumber] = useState<number>(1);
  const [mode, setMode] = useState<Modes>(Modes.Title);
  const [ease, setEase] = useState<number>(LONG_EASE); //for how long the bar chart takes to move
  const [displayLabels, setDisplayLabels] = useState<boolean>(false);

  // Detect referendum-style standings (no explicit type in data files)
  const isReferendum = /referendum/i.test(standing.name);

  const onNext = useCallback(() => {
    switch (mode) {
      case Modes.Title:
        //Introduce Round
        setMode(Modes.Candidates);
        break;
      case Modes.Candidates:
        //Introduce Candidates
        setMode(Modes.PreAscending);
        setEase(SHORT_EASE);
        break;
      case Modes.PreAscending:
        //Introduce Graph
        setMode(Modes.Ascending);
        setEase(LONG_EASE);
        break;
      case Modes.Ascending:
        //Show results
        setMode(Modes.Descending);
        setEase(SHORT_EASE);
        break;
      case Modes.Descending:
        //Remove eliminated candidates
        if (roundNumber < size(standing.rounds)) {
          setRoundNumber(Math.min(roundNumber + 1, size(standing.rounds)));
          setMode(Modes.PreAscending);
        } else {
          setMode(Modes.Winner);
        }
        break;
      default:
        // nothing to update
        return false;
    }
  }, [mode, setMode]);

  useEffect(() => {
    setDisplayLabels(false);
    //This component has sub slides that we handle, so tell the slideshow comp that
    if (registerNextSlideHandler) {
      registerNextSlideHandler(onNext);
    }
  }, [mode]);

  ////////////////////////// Dev Timer //////////////////////////

  // const timerRef = useRef<NodeJS.Timeout | null>(null);
  //
  // useEffect(() => {
  //   timerRef.current = setInterval(() => {
  //     onNext();
  //   }, 2000);
  //
  //   return () => {
  //     if (timerRef.current) {
  //       clearInterval(timerRef.current);
  //     }
  //   };
  // }, [mode]);

  ////////////////////////// End of Dev Timer //////////////////////////

  let content = <></>;

  // console.log(mode);

  // Get actual vote values
  let quota = 100000;
  const voteData: number[] = [];
  const candidates: string[] = [];
  forEach(standing.candidates, (candidate, key) => {
    if (standing.rounds[roundNumber].votes[key]) {
      //add name
      candidates.push(candidate.short_name);
      //set vote data
      if (mode === Modes.Ascending) {
        quota = standing.rounds[roundNumber].quota;
        voteData.push(standing.rounds[roundNumber].votes[key]);
      } else if (mode === Modes.PreAscending) {
        quota = standing.rounds[roundNumber].quota;
        voteData.push(0);
      } else if (mode === Modes.Descending) {
        quota = standing.rounds[roundNumber].quota;
        //either remove eliminated or non winners
        if (standing.rounds[roundNumber].winners) {
          if (standing.rounds[roundNumber].winners.includes(key)) {
            voteData.push(standing.rounds[roundNumber].votes[key]);
          } else {
            voteData.push(0);
          }
        } else {
          //if they're eliminated return 0 to remove from chart, else return the vote value
          if (
            standing.rounds[roundNumber].eliminated &&
            standing.rounds[roundNumber].eliminated.includes(parseInt(key))
          ) {
            voteData.push(0);
          } else {
            voteData.push(standing.rounds[roundNumber].votes[key]);
          }
        }
      } else {
        //other modes we don't want any results so return 0;
        voteData.push(0);
      }
    }
  });

  //Round Labelling
  const words = [
    "First",
    "Second",
    "Third",
    "Fourth",
    "Fifth",
    "Sixth",
    "Seventh",
    "Eighth",
    "Nineth",
    "Tenth",
  ];

  //Slide Switcher
  switch (mode) {
    case Modes.Title:
      content = (
        <CSSTransition
          key={mode}
          classNames="slide-transition"
          timeout={{ enter: 1000, exit: 1000 }}
        >
          <div key="title" className={`positionMode positionMode--${mode}`}>
            <h1>{standing.name}</h1>
            {/* <span className="hashtag">
              {getStyleVariable("--hashtag-text")}
            </span> */}
          </div>
        </CSSTransition>
      );
      break;
    case Modes.Candidates:
      content = (
        <CSSTransition
          key={mode}
          classNames="slide-transition"
          timeout={{ enter: 1000, exit: 1000 }}
        >
          <div
            key="candidates"
            className={`positionMode positionMode--${mode}`}
          >
            <h1>{isReferendum ? "Options" : "Candidates"}</h1>
            <ul
              className={`candidates ${
                isReferendum ? "candidates--referendum" : ""
              }`}
            >
              {map(standing.candidates, (c, idx) => (
                <li key={idx} className="candidate">
                  <div className="candidateImage">
                    <img
                      alt=""
                      src={`/assets/candidates/${c.photo}`}
                      loading="eager"
                    />
                  </div>
                  <span className="candidateName">{c.name}</span>
                </li>
              ))}
            </ul>
          </div>
        </CSSTransition>
      );
      break;
    case Modes.PreAscending:
    case Modes.Ascending:
    case Modes.Descending:
      content = (
        <div key="votes" className={`positionMode positionMode--${mode}`}>
          <div style={{ height: `${getSizeScaledFromFullHD(160)}px` }} />
          <BarChartD3Framer
            data={candidates.map((name, idx) => ({
              name,
              value: voteData[idx],
            }))}
            mode={mode}
            quota={quota}
            showLabels={true}
            curtainReveal={true}
            height={getSizeScaledFromFullHD(640)}
            width={getSizeScaledFromFullHD(1200)}
          />

          <h1>{standing.name}</h1>
          <div className="round">
            <TransitionGroup>
              <CSSTransition
                classNames="transition-round-number"
                timeout={{ enter: 1000, exit: 1000 }}
              >
                <span key={roundNumber}>{words[roundNumber - 1]} round</span>
              </CSSTransition>
            </TransitionGroup>
          </div>
          <div className="quota">
            <strong>Quota {standing.rounds[roundNumber].quota} votes</strong>
          </div>
        </div>
      );
      break;
    case Modes.Winner:
      content = (
        <CSSTransition
          key={mode}
          classNames="slide-transition"
          timeout={{ enter: 1000, exit: 1000 }}
        >
          <div key="title" className={`positionMode positionMode--${mode}`}>
            <div className="winners-container">
              <h1>
                {standing.rounds[roundNumber].winners.length > 1
                  ? "Winners"
                  : "Winner"}
              </h1>
              <h2>{standing.name}</h2>
              <ul
                className={`candidates candidates--winners ${
                  isReferendum ? "candidates--referendum" : ""
                }`}
              >
                {standing.rounds[roundNumber].winners.map((key) => (
                  <li className="candidate" key={key}>
                    <div className="candidateImage">
                      <img
                        alt=""
                        src={`/assets/candidates/${standing.candidates[key].photo}`}
                      />
                    </div>
                    <span className="candidateName">
                      {standing.candidates[key].name}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </CSSTransition>
      );
      break;
    default:
      content = <p>Something Went Wrong</p>;
      break;
  }
  return (
    <div>
      <TransitionGroup>{content}</TransitionGroup>
    </div>
  );
};

export default Standing;
